import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { DOCUMENT_ACCEPT } from '../../../utils/documentAccept';
import PersonCell from '../../../components/ui/PersonCell';

// EmployeeDocumentRequirement modelindeki durum makinesiyle AYNI küme. Karşılanmış sayılan tek
// iki durum approved/waived — DocumentMissingRule.SatisfiedStatuses ile aynı tanım.
export const STATUSES = ['pending', 'uploaded', 'approved', 'rejected', 'waived', 'cancelled'];
// PbDocumentRequirement'ın tür sözlüğüyle ortak olanlar + süregelen istihdama özgü olanlar
// (çalışma izni, sağlık raporu, ehliyet — hepsinin SON KULLANMA tarihi olabilir).
export const DOC_TYPES = [
  'id_card', 'work_permit', 'health_report', 'driver_license', 'diploma',
  'criminal_record', 'kvkk_consent',
  // PB sözlüğüyle ortak küme — işe hazırlık kapanışındaki devirde tür eşlemesi birebir olsun
  // diye eklendi (bkz. özlük dosyası spec §7).
  'employment_contract', 'nda', 'residence', 'iban', 'sgk_form',
  'emergency_contact', 'vehicle', 'private_health',
  // Arşiv türleri — bir çalışanda birden çok kayıt olabilir (backend EmployeeDocumentTypes.MultiInstance).
  'leave_form', 'isg_training', 'isg_medical', 'certificate',
  'custom',
];
export const KVKK_CATS = ['employment_records', 'health', 'communication', 'financial'];

const multiSelectFilter = (label, options, valueRender) => ({
  filter: true,
  sort: true,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(selected) => selected.map((s) => (valueRender ? valueRender(s) : s)).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{valueRender ? valueRender(o) : o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  },
});

const useEmployeeDocumentsApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 25, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const merged = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : (lastValidQueryParams.current || { pageNumber: 1, pageSize: 25 });
    lastValidQueryParams.current = merged;
    setFetching(true);
    api.get(`/v1/employee-document-requirements?${new URLSearchParams(merged)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 25,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const create = useCallback(async (formData) => {
    try {
      await api.post('/v1/employee-document-requirements', {
        employeeId: formData.employeeId,
        documentType: formData.documentType || 'custom',
        title: formData.title || '',
        isMandatory: !!formData.isMandatory,
        requiresHrApproval: !!formData.requiresHrApproval,
        kvkkCategory: formData.kvkkCategory || 'employment_records',
        validUntil: formData.validUntil || null,
        notesMarkdown: formData.notesMarkdown || null,
      });
      refresh();
      return true;
    } catch { return false; }
  }, [refresh]);

  const upload = useCallback(async (id, formData) => {
    try {
      await api.post(`/v1/employee-document-requirements/${id}/upload`, {
        fileUrl: formData.fileUrl || null,
      });
      refresh();
      return true;
    } catch { return false; }
  }, [refresh]);

  const decide = useCallback(async (id, formData) => {
    try {
      const approved = formData.decision === 'approve';
      await api.post(`/v1/employee-document-requirements/${id}/decide`, {
        approved,
        rejectedReason: approved ? null : (formData.rejectedReason || null),
        validUntil: approved ? (formData.validUntil || null) : null,
      });
      refresh();
      return true;
    } catch { return false; }
  }, [refresh]);

  const waive = useCallback(async (id) => {
    try { await api.post(`/v1/employee-document-requirements/${id}/waive`, {}); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const cancel = useCallback(async (id) => {
    try { await api.post(`/v1/employee-document-requirements/${id}/cancel`, {}); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const columns = useMemo(() => {
    const typeLabel = (s) => t(`employeeDocument.type.${s}`, { defaultValue: s });
    const statusLabel = (s) => t(`employeeDocument.status.${s}`, { defaultValue: s });
    const kvkkLabel = (s) => t(`employeeDocument.kvkk.${s}`, { defaultValue: s });

    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      { name: 'employeeId', label: 'EMP', options: { filter: false, sort: false, display: 'excluded' } },
      {
        // Müşteri şikayeti: liste kimin belgesi olduğunu göstermiyor VE aranamıyordu.
        // `employeeFullName[like]` backend'de EmployeeDocumentRequirementFilterRequest
        // üzerinden bağlanıyor (bkz. task-12-brief §Interfaces / Task 1).
        name: 'employeeFullName', label: t('employeeDocument.employee'),
        options: {
          customBodyRender: (v, tableMeta) => (
            <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.employeeProfilePicture} />
          ), filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
      },
      {
        name: 'title', label: t('employeeDocument.title'),
        options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
      },
      {
        name: 'documentType', label: t('employeeDocument.typeLabel'),
        options: {
          ...multiSelectFilter(t('employeeDocument.typeLabel'), DOC_TYPES, typeLabel),
          customBodyRender: (v) => typeLabel(v),
        },
      },
      {
        name: 'status', label: t('employeeDocument.statusLabel'),
        options: {
          ...multiSelectFilter(t('employeeDocument.statusLabel'), STATUSES, statusLabel),
          customBodyRender: (v) => statusLabel(v),
        },
      },
      {
        name: 'isMandatory', label: t('employeeDocument.isMandatory'),
        options: { filter: true, sort: true, customBodyRender: (v) => (v ? t('common.yes') : t('common.no')) },
      },
      dateFilterColumn('validUntil', t('employeeDocument.validUntil'), lang),
      {
        // Onaylı olsa bile süresi geçmiş belge kural tarafından EKSİK sayılır — ekranda da
        // ayrıca görünmeli, yoksa "approved" satırın neden uyarı ürettiği anlaşılmaz.
        name: 'isExpired', label: t('employeeDocument.isExpired'),
        options: { filter: false, sort: false, customBodyRender: (v) => (v ? t('common.yes') : t('common.no')) },
      },
      {
        name: 'kvkkCategory', label: t('employeeDocument.kvkkLabel'),
        options: {
          ...multiSelectFilter(t('employeeDocument.kvkkLabel'), KVKK_CATS, kvkkLabel),
          customBodyRender: (v) => kvkkLabel(v),
        },
      },
    ];
  }, [t, lang, data]);

  const createSchema = useMemo(() => {
    const typeLabel = (s) => t(`employeeDocument.type.${s}`, { defaultValue: s });
    const kvkkLabel = (s) => t(`employeeDocument.kvkk.${s}`, { defaultValue: s });
    return [
      {
        name: 'employeeId', label: t('employeeDocument.employee'), type: 'asyncSelect', required: true,
        onSearch: async (q) => {
          const params = q ? { 'firstName[like]': q, pageSize: 20 } : { pageSize: 20 };
          const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
          const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
          return items.map((e) => ({ id: e.id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture }));
        },
        onGetById: async (id) => {
          const res = await api.get(`/v1/employees/${id}`);
          const e = res.data?.data || {};
          return { id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture };
        },
      },
      { name: 'title', label: t('employeeDocument.title'), type: 'text', required: true },
      {
        name: 'documentType', label: t('employeeDocument.typeLabel'), type: 'select', grid: 6,
        options: DOC_TYPES.map((v) => ({ id: v, name: typeLabel(v) })),
      },
      {
        name: 'kvkkCategory', label: t('employeeDocument.kvkkLabel'), type: 'select', grid: 6,
        options: KVKK_CATS.map((v) => ({ id: v, name: kvkkLabel(v) })),
      },
      // Boş = süresiz belge (diploma, imzalı taahhüt). Doldurulursa süre dolunca kural yeniden
      // uyarır — bu alanın bütün varlık sebebi budur.
      { name: 'validUntil', label: t('employeeDocument.validUntilOptional'), type: 'date' },
      { name: 'isMandatory', label: t('employeeDocument.isMandatory'), type: 'checkbox' },
      { name: 'requiresHrApproval', label: t('employeeDocument.requiresHrApproval'), type: 'checkbox' },
      { name: 'notesMarkdown', label: t('employeeDocument.notes'), type: 'textarea' },
    ];
  }, [t]);

  // GERÇEK DOSYA YÜKLEME (müşteri bildirimi 2026-08-18). Buradaki alan serbest metin bir adres
  // kutusuydu; İK'dan bir depolama adresi yazmasını beklemek kullanılabilir bir akış olmadığı
  // gibi, 2026-08-13'te eklenen yazma-yolu kapısından sonra ARTIK ÇALIŞMIYORDU da: elle yazılan
  // hiçbir adresin ihraç kaydı olmadığı için uç her seferinde `employeeDocument.fileUrlInvalid`
  // döndürüyordu. `pictureUpload` tipi presign → doğrudan PUT → adres akışını GenericFormDrawer
  // içinde yürütür (bkz. uploadSchemaFiles), yani `onSave`e ulaşan formData'da `fileUrl` zaten
  // ihraç edilmiş gerçek adrestir. `purpose` ZORUNLU: özel kovayı ve dolayısıyla imzalı erişimi
  // o seçer — bildirilmezse sunucu Private varsayar ama niyeti açıkça yazmak driftten korur.
  const uploadSchema = useMemo(() => [
    {
      name: 'fileUrl',
      label: t('employeeDocument.file'),
      type: 'pictureUpload',
      required: true,
      purpose: 'hrDocument',
      accept: DOCUMENT_ACCEPT,
      maxSize: 10,
    },
  ], [t]);

  const decideSchema = useMemo(() => [
    {
      name: 'decision', label: t('employeeDocument.decisionLabel'), type: 'select', required: true,
      options: [
        { id: 'approve', name: t('employeeDocument.decision.approve') },
        { id: 'reject', name: t('employeeDocument.decision.reject') },
      ],
    },
    // Reddetmede zorunlu, onayda yok sayılır — tek doğrulama kaynağı backend.
    { name: 'rejectedReason', label: t('employeeDocument.rejectedReason'), type: 'textarea' },
    // Onayda yeni geçerlilik tarihi (yenilenen kimlik yeni tarih taşır).
    { name: 'validUntil', label: t('employeeDocument.validUntilOptional'), type: 'date' },
  ], [t]);

  return {
    fetching, data, pagination, columns,
    createSchema, uploadSchema, decideSchema,
    refresh, create, upload, decide, waive, cancel,
  };
};

export default useEmployeeDocumentsApi;
